import {
  CreateGUFExpandedRealisedInstallationTask,
  EditGUFExpandedRealisedInstallationTask,
} from "@nens/brostar/uploadapi-client";
import { SchemaForFormData } from "@nens/shared-utils";

import { getDocsLinkMarkdown, getTitleAddon } from "@/libs/form-utils";
import { FormType } from "@/types/form-types";

import { pickGufLibraryFields } from "./guf-schema-library";

const sharedTitle = (type: FormType | undefined) =>
  `GUF Uitgebreide gerealiseerde installatie ${getTitleAddon(type)}`;
const sharedDescription = `Dit is het GUF uitgebreide gerealiseerde installatie formulier. Let op: dit verschilt van "Gerealiseerde installatie toevoegen" — gebruik dit formulier om een bestaande installatie uit te breiden. ${getDocsLinkMarkdown(
  "GUF"
)}`;

const sharedSourcedocumentData = {
  type: "object" as const,
  title: "Brondocumentgegevens",
  required: ["realisedInstallationId", "installationFunction", "realisedLoopPos", "startValidity"],
  properties: {
    ...pickGufLibraryFields([
      "realisedInstallationId",
      "installationFunction",
      "realisedLoopPos",
      "startValidity",
      "realisedLoops",
      "realisedSurfaceInfiltrations",
    ]),
    // validity and lifespan are NOT allowed for ExpandedRealisedInstallation
    realisedWells: pickGufLibraryFields(["realisedWellsExpanded"]).realisedWellsExpanded,
  },
};

const getGufExpandedRealisedInstallationCreateSchema = (
  type: FormType | undefined
): SchemaForFormData<CreateGUFExpandedRealisedInstallationTask> => ({
  type: "object",
  title: sharedTitle(type),
  description: sharedDescription,
  required: ["metadata", "sourcedocumentData"],
  properties: {
    metadata: {
      type: "object",
      title: "Metadata",
      required: ["projectNumber", "deliveryAccountableParty", "qualityRegime", "broId"],
      properties: pickGufLibraryFields([
        "projectNumber",
        "qualityRegime",
        "deliveryAccountableParty",
        "broId",
      ]),
    },
    sourcedocumentData: sharedSourcedocumentData,
  },
});

const getGufExpandedRealisedInstallationEditSchema = (
  type: FormType | undefined
): SchemaForFormData<EditGUFExpandedRealisedInstallationTask> => ({
  type: "object",
  title: sharedTitle(type),
  description: sharedDescription,
  required: ["metadata", "sourcedocumentData"],
  properties: {
    metadata: {
      type: "object",
      title: "Metadata",
      required: [
        "projectNumber",
        "deliveryAccountableParty",
        "qualityRegime",
        "broId",
        "correctionReason",
      ],
      properties: {
        ...pickGufLibraryFields([
          "projectNumber",
          "qualityRegime",
          "deliveryAccountableParty",
          "correctionReason",
        ]),
        broId: { ...pickGufLibraryFields(["broId"]).broId, readOnly: true },
      },
    },
    sourcedocumentData: sharedSourcedocumentData,
  },
});

export function getGufExpandedRealisedInstallationSchema(args: {
  type: FormType | undefined;
  isEditing: true;
}): SchemaForFormData<EditGUFExpandedRealisedInstallationTask>;
export function getGufExpandedRealisedInstallationSchema(args: {
  type: FormType | undefined;
  isEditing: false;
}): SchemaForFormData<CreateGUFExpandedRealisedInstallationTask>;
export function getGufExpandedRealisedInstallationSchema(args: {
  type: FormType | undefined;
  isEditing: boolean;
}):
  | SchemaForFormData<EditGUFExpandedRealisedInstallationTask>
  | SchemaForFormData<CreateGUFExpandedRealisedInstallationTask>;
export function getGufExpandedRealisedInstallationSchema({
  type,
  isEditing,
}: {
  type: FormType | undefined;
  isEditing: boolean;
}) {
  return isEditing
    ? getGufExpandedRealisedInstallationEditSchema(type)
    : getGufExpandedRealisedInstallationCreateSchema(type);
}
